import dayjs from "dayjs";

/*
 * When a purchase document may still be called off.
 *
 * The rule is the same for a purchase entry, a purchase order and a purchase
 * return, so it is written once here rather than three times over:
 *
 *   draft       any time - nothing has moved yet
 *   completed   within 24 hours of being submitted, and no longer
 *   cancelled   never again
 *
 * A Pharmacy Admin is not held to the window; the endpoint enforces that too,
 * so the screen and the server agree about who may do what.
 *
 * The server does the arithmetic and sends `editable_until` alongside
 * `submitted_at`; that figure is believed where it is given, and worked out
 * from `submitted_at` only where it is not. A completed document carrying
 * neither cannot be shown to be inside the window, so it is treated as
 * outside it - an admin can still call it off.
 */

const CANCEL_WINDOW_HOURS = 24;

/** The moment a completed document stops being cancellable, if it is known. */
export const cancelDeadline = (doc) => {
  if (doc?.editable_until) return dayjs(doc.editable_until);
  if (doc?.submitted_at) {
    return dayjs(doc.submitted_at).add(CANCEL_WINDOW_HOURS, "hour");
  }
  return null;
};

/** Whether the deadline has passed. Unknown deadlines read as passed. */
export const isWithinCancelWindow = (doc) => {
  const deadline = cancelDeadline(doc);
  return Boolean(deadline && dayjs().isBefore(deadline));
};

/**
 * @param doc       the entry, order or return as the server sent it
 * @param isAdmin   whether the person looking is a Pharmacy Admin
 */
export const canCancelDocument = (doc, { isAdmin = false } = {}) => {
  if (!doc?.id) return false;
  if (doc.status === "cancelled") return false;
  if (doc.status === "draft") return true;
  return isAdmin || isWithinCancelWindow(doc);
};

/**
 * Whether a document may still be rewritten, which is the same question as
 * whether it may still be called off and is answered the same way: a draft
 * any time, a completed one inside the day it was submitted, a Pharmacy Admin
 * whenever. A cancelled one, never.
 *
 * It was briefly narrowed to drafts alone, which took the pencil and the bin
 * off a completed entry the moment it was completed - inside its window as
 * well as outside it. The window is the point: a mistake spotted the same
 * afternoon is put right, and after that only an admin can.
 */
export const canEditDocument = canCancelDocument;


export const cancelWindowNote = (doc, { isAdmin = false } = {}) => {
  if (!doc?.id || doc.status === "draft") return null;

  const deadline = cancelDeadline(doc);
  if (!deadline) return null;

  if (isAdmin && !dayjs().isBefore(deadline)) {
    return `The 24-hour window closed on ${deadline.format(
      "DD MMM YYYY, hh:mm A"
    )}; you can cancel this as a Pharmacy Admin.`;
  }

  return `It was submitted ${dayjs(
    doc.submitted_at || deadline
  ).format("DD MMM YYYY, hh:mm A")} and can be cancelled until ${deadline.format(
    "DD MMM YYYY, hh:mm A"
  )}.`;
};



const refusalLines = (error) => {
  const data = error?.response?.data;
  if (!data) return [];
  if (typeof data === "string") return [data];

  const value = Array.isArray(data)
    ? data
    : data.detail ?? data.non_field_errors ?? data.errors ?? data.message;

  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.filter((line) => typeof line === "string");
  return [];
};

const SHORTFALL =
  /already been sold|only \d+(\.\d+)? (are|is) in stock|not enough stock|insufficient stock/i;

const medicineNamed = (line) => {
  const name = String(line).split(":")[0].split("/")[0].trim();
  return name && name.length <= 80 ? name : null;
};

const listOf = (names) => {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
};

export const cancelFailureMessage = (error, fallback) => {
  const lines = refusalLines(error);
  const shortfalls = lines.filter((line) => SHORTFALL.test(line));

  if (shortfalls.length) {
    const names = [...new Set(shortfalls.map(medicineNamed).filter(Boolean))];
    const subject = names.length ? listOf(names) : "Some of this stock";
    const verb = names.length > 1 ? "have" : "has";
    return `${subject} ${verb} already been sold or moved out of stock, so this can no longer be cancelled.`;
  }

  return lines[0] || fallback;
};

/*
 * Why a save was refused, in words a pharmacist can act on.
 *
 * The server refuses two things often enough to be worth naming, and says so
 * in its own vocabulary - a row that is referenced elsewhere, or a batch with
 * less in it than the line is asking for. Left as they arrive, both read as
 * "failed to save", which tells nobody what to do next.
 *
 * Anything else is passed through exactly as the server said it: a refusal
 * this does not recognise is still the server's to explain.
 */

/*
 * The server's own words for both refusals, which are the same words.
 *
 * It answers a line it cannot unwind with the arithmetic behind it - "needs
 * 999998 units from batch 123, but only 0 are in stock - the rest has already
 * been sold, returned or transferred." That one sentence covers a purchase
 * entry whose stock has gone and a return asking for more than there is, so
 * the sentence alone cannot say which screen is asking. The caller says.
 */
const STOCK_GONE =
  /only \d+(\.\d+)? (are|is) (in stock|available)|already been (sold|ordered|dispensed|billed|issued)|not enough stock|insufficient stock|more than .*stock|exceeds? .*(stock|available)/i;

/**
 * @param returning  true on a purchase return, where the refusal is about the
 *                   quantity being sent back rather than about the medicine
 */
export const purchaseFailureMessage = (error, fallback, { returning = false } = {}) => {
  const lines = refusalLines(error);
  const said = lines.join(" ");

  if (STOCK_GONE.test(said)) {
    return returning
      ? "The returned quantity is more than the stock."
      : "Cannot update medicine details as this medicine has already been ordered or sold.";
  }

  /* Anything else is the server's to explain, and it explains it well - "A
     completed purchase entry must keep at least one item." reads better than
     anything this could put in its place. */
  return lines[0] || fallback;
};
